import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUndo,
    faEye,
    faSearch,
    faRefresh,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faBoxes,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faTimesCircle,
    faBox,
    faTag,
    faBuilding,
    faDollarSign,
    faCalculator,
    faHashtag,
    faUser,
    faFileInvoice,
    faHistory,
    faReceipt,
    faMoneyBill,
    faPercent,
    faPhone,
    faMapMarkerAlt,
    faStore,
    faChartLine,
    faArrowsAlt,
    faExpand,
    faCompress,
    faMinus,
    faPlus,
    faList,
    faInfoCircle,
    faClock,
    faCalendarWeek,
    faChartBar,
    faRulerCombined,
    faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createFabReturnListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_fab_return_list_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Fab Return List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_fab_return_list_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        returnList: 'Fabric Return List',
        allReturns: 'All Returns',
        searchPlaceholder: 'Search by return number, invoice, customer...',
        refresh: 'Refresh',
        loadingReturns: 'Loading returns...',
        noReturnsFound: 'No returns found',
        returnDetails: 'Return Details',
        close: 'Close',
        serialNo: 'S.No',
        id: 'ID',
        returnNumber: 'Return #',
        returnDate: 'Return Date',
        originalBillNumber: 'Original Invoice',
        customerName: 'Customer Name',
        customerPhone: 'Phone',
        subtotal: 'Subtotal',
        discountAmount: 'Discount',
        taxAmount: 'Tax',
        grandTotal: 'Grand Total',
        refundAmount: 'Refund Amount',
        paymentMethod: 'Payment Method',
        returnReason: 'Return Reason',
        notes: 'Notes',
        addedBy: 'Added By',
        shopInfo: 'Shop',
        view: 'View',
        actions: 'Actions',
        noShopSelected: 'No shop selected. Please select a shop first.',
        networkError: 'Network error occurred',
        showing: 'Showing',
        of: 'of',
        returns: 'returns',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        filterBy: 'Filter By',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'PDF',
        printList: 'Print List',
        exportData: 'Export Data',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        reportFilters: 'Applied Filters',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalReturns: 'Total Returns',
        totalRefundAmount: 'Total Refund',
        averageRefund: 'Avg Refund',
        todayReturns: "Today's Returns",
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        itemsReturned: 'Items Returned',
        productName: 'Product',
        quantity: 'Qty',
        rolls: 'Rolls',
        totalYards: 'Total Yards',
        totalMeters: 'Total Meters',
        price: 'Price',
        total: 'Total',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        wallet: 'Wallet',
        returnReceipt: 'Return Receipt',
        printedBy: 'Printed by',
        thankYou: 'Thank you for your business!',
        poweredBy: 'Powered by OurZest',
        businessInfo: 'Business Information',
        cashier: 'Cashier',
        transactionId: 'Transaction ID',
        itemsReturnedList: 'Items Returned'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const fabReturnListTranslator = createFabReturnListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 450, label: 'Narrow', icon: faMinus },
    medium: { width: 700, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 900, label: 'Wide', icon: faPlus },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

const FabReturnList = () => {
    const { user } = useAuth();
    const [returns, setReturns] = useState([]);
    const [filteredReturns, setFilteredReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [currentShop, setCurrentShop] = useState(null);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 700, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // PDF Options State
    const [pdfOrientation, setPdfOrientation] = useState('portrait');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    // Filter states
    const [filterType, setFilterType] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Get theme and language
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Calculate totals
    const totalReturns = filteredReturns.length;
    const totalRefundAmount = filteredReturns.reduce((sum, ret) => sum + (parseFloat(ret.refund_amount) || 0), 0);
    const averageRefund = totalReturns > 0 ? totalRefundAmount / totalReturns : 0;

    const totalPages = Math.ceil(filteredReturns.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentReturns = filteredReturns.slice(startIndex, endIndex);

    // Get rows per page based on orientation and page size
    const getRowsPerPage = () => {
        if (pdfPageSize === 'a5') return pdfOrientation === 'portrait' ? 25 : 30;
        if (pdfPageSize === 'legal') return pdfOrientation === 'portrait' ? 30 : 35;
        if (pdfPageSize === 'letter') return pdfOrientation === 'portrait' ? 28 : 32;
        return pdfOrientation === 'portrait' ? 25 : 30;
    };

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha = 1) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Show toast function
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await fabReturnListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();
        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Fetch returns on mount
    useEffect(() => {
        if (user && user.username) {
            fetchReturns();
            fetchBusinessInfo();
            
            const shopId = localStorage.getItem('current_shop_id');
            const shopName = localStorage.getItem('current_shop_name');
            if (shopId) {
                setCurrentShop({ id: shopId, name: shopName });
            }
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [returns, searchTerm, filterType, startDate, endDate]);

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode
    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
            modalRef.current.style.maxWidth = '98vw';
            modalRef.current.style.maxHeight = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
            modalRef.current.style.maxWidth = '95vw';
            modalRef.current.style.maxHeight = '85vh';
        }
        
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    // Start dragging
    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern')) {
            return;
        }
        
        e.preventDefault();
        setIsDragging(true);
        
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('dragging');
    }, []);

    // Start resizing
    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!modalRef.current) return;
        
        setIsResizing(true);
        resizeType.current = type;
        
        const rect = modalRef.current.getBoundingClientRect();
        
        resizeStart.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 350,
            minHeight: 450,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalPosition({ x: newLeft, y: newTop });
            }
            
            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;
                
                switch (resizeType.current) {
                    case 'e':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        break;
                    case 'w':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                    case 's':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'n':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'ne':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'nw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'se':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'sw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                }
                
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                
                if (modalRef.current) {
                    modalRef.current.style.transition = '';
                    modalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDragging, isResizing]);

    // Initialize modal position when opened
    useEffect(() => {
        if (showViewModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => {
                centerModal();
                setWidthMode('medium');
                setIsModalInitialized(true);
            }, 50);
        }
        
        if (!showViewModal) {
            setIsModalInitialized(false);
        }
    }, [showViewModal, centerModal, setWidthMode, isModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showViewModal) return;
            
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showViewModal]);

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
        }
    };

    // Calculate totals for an item (meters, yards, rolls)
    const calculateItemTotals = (items) => {
        let totalRolls = 0;
        let totalYards = 0;
        let totalMeters = 0;
        
        if (items && items.length > 0) {
            items.forEach(item => {
                totalRolls += parseInt(item.rolls) || 0;
                
                // Calculate yards based on unit
                if (item.unit === 'yards') {
                    totalYards += parseFloat(item.quantity) || 0;
                    totalMeters += (parseFloat(item.quantity) || 0) * 0.9144;
                } else if (item.unit === 'meters') {
                    totalMeters += parseFloat(item.quantity) || 0;
                    totalYards += (parseFloat(item.quantity) || 0) * 1.09361;
                }
            });
        }
        
        return { totalRolls, totalYards: totalYards.toFixed(2), totalMeters: totalMeters.toFixed(2) };
    };

    // Fetch returns from backend
    const fetchReturns = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available');
            setLoading(false);
            return;
        }

        const currentShopId = localStorage.getItem('current_shop_id') || user.shop_id;
        
        if (!currentShopId) {
            showToast('error', translations.noShopSelected || 'No shop selected');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_return.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setReturns(data.data);
                if (data.data.length === 0) {
                    showToast('info', translations.noReturnsFound || 'No returns found in this shop');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching returns:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    // Apply filters
    const applyFilters = () => {
        let filtered = [...returns];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(ret =>
                ret.return_number?.toLowerCase().includes(term) ||
                ret.original_bill_number?.toLowerCase().includes(term) ||
                ret.customer_name?.toLowerCase().includes(term) ||
                ret.customer_phone?.toLowerCase().includes(term) ||
                ret.username?.toLowerCase().includes(term)
            );
        }

        if (filterType === 'today') {
            const today = new Date().toISOString().split('T')[0];
            filtered = filtered.filter(ret => ret.return_date === today);
        } else if (filterType === 'week') {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            filtered = filtered.filter(ret => new Date(ret.return_date) >= weekAgo);
        } else if (filterType === 'month') {
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            filtered = filtered.filter(ret => new Date(ret.return_date) >= monthAgo);
        }

        if (startDate) {
            filtered = filtered.filter(ret => ret.return_date >= startDate);
        }

        if (endDate) {
            filtered = filtered.filter(ret => ret.return_date <= endDate);
        }

        setFilteredReturns(filtered);
        setCurrentPage(1);
    };

    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setFilterType('all');
        setStartDate('');
        setEndDate('');
        applyFilters();
    };

    // Handle view return details
    const handleView = async (returnRecord) => {
        setSelectedReturn(returnRecord);
        setShowViewModal(true);
        
        // Fetch items for this return if not already loaded
        if (!returnRecord.items && returnRecord.id) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/fab_return.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'get_return_items',
                        return_id: returnRecord.id,
                        shop_id: returnRecord.shop_id
                    })
                });
                const data = await response.json();
                if (data.status === 'success' && data.items) {
                    setSelectedReturn(prev => ({ ...prev, items: data.items }));
                }
            } catch (error) {
                console.error('Error fetching return items:', error);
            }
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0.00';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    // Format number with 2 decimals
    const formatNumber = (num) => {
        if (!num && num !== 0) return '0.00';
        return parseFloat(num).toFixed(2);
    };

    // Get payment method icon
    const getPaymentIcon = (method) => {
        switch (method?.toLowerCase()) {
            case 'cash': return faMoneyBill;
            case 'card': return faCreditCard;
            case 'upi': return faQrcode;
            default: return faWallet;
        }
    };

    // Get today's date for date inputs
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Set max date for end date
    useEffect(() => {
        if (startDate && endDateRef.current) {
            endDateRef.current.min = startDate;
        }
    }, [startDate]);

    // Set max date for start date to today
    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) {
            startDateRef.current.max = today;
        }
        if (endDateRef.current) {
            endDateRef.current.max = today;
        }
    }, []);

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Export to PDF
    const handleExportPDF = async () => {
        if (filteredReturns.length === 0) {
            showToast('error', translations.noReturnsFound || 'No data to export!');
            return;
        }

        setPdfLoading(true);

        try {
            const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
            const primaryColor = currentTheme || '#10b981';

            const pageSizeMap = {
                'a4': 'a4',
                'letter': 'letter',
                'legal': 'legal',
                'a5': 'a5'
            };

            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';

            const pdf = new jsPDF({
                orientation: pdfOrientation,
                unit: 'mm',
                format: pageFormat,
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const rowsPerPage = getRowsPerPage();
            
            let returnsToExport = [...filteredReturns];
            
            if (pdfPageRange === 'range') {
                const totalPagesCount = Math.ceil(returnsToExport.length / rowsPerPage);
                const start = Math.max(1, pdfStartPage);
                const end = Math.min(totalPagesCount, pdfEndPage);
                const startIdx = (start - 1) * rowsPerPage;
                const endIdx = Math.min(end * rowsPerPage, returnsToExport.length);
                returnsToExport = returnsToExport.slice(startIdx, endIdx);
            }

            const totalPagesCount = Math.ceil(returnsToExport.length / rowsPerPage);

            const filterInfoHTML = (searchTerm || startDate || endDate || filterType !== 'all') ? `
                <div style="margin: 8px 0; padding: 6px; background: #f9fafb; border-radius: 6px; font-size: 8px; color: #6b7280; text-align: center;">
                    ${translations.reportFilters || 'Applied Filters'}: 
                    ${searchTerm ? `Search: ${searchTerm}` : ''}
                    ${startDate ? ` | From: ${startDate}` : ''}
                    ${endDate ? ` | To: ${endDate}` : ''}
                    ${filterType !== 'all' ? ` | Period: ${filterType === 'today' ? 'Today' : filterType === 'week' ? 'This Week' : 'This Month'}` : ''}
                </div>
            ` : '';

            const summaryStatsHTML = `
                <div style="display: flex; justify-content: center; gap: 20px; margin: 10px 0; flex-wrap: wrap;">
                    <div style="background: ${hexToRgba(primaryColor, 0.05)}; padding: 5px 15px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        <div style="font-size: 10px; color: #666;">${translations.totalReturns || 'Total Returns'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: ${primaryColor};">${returnsToExport.length}</div>
                    </div>
                    <div style="background: ${hexToRgba(primaryColor, 0.05)}; padding: 5px 15px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        <div style="font-size: 10px; color: #666;">${translations.totalRefundAmount || 'Total Refund'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: #059669;">${formatCurrency(returnsToExport.reduce((sum, r) => sum + (parseFloat(r.refund_amount) || 0), 0))}</div>
                    </div>
                    <div style="background: #fef3c7; padding: 5px 15px; border-radius: 20px; border: 1px solid rgba(245, 158, 11, 0.2);">
                        <div style="font-size: 10px; color: #92400e;">${translations.averageRefund || 'Avg Refund'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: #d97706;">${formatCurrency(returnsToExport.reduce((sum, r) => sum + (parseFloat(r.refund_amount) || 0), 0) / (returnsToExport.length || 1))}</div>
                    </div>
                </div>
            `;

            const addPatternBackground = (pdfDoc) => {
                pdfDoc.setFillColor(250, 251, 252);
                pdfDoc.rect(0, 0, pageWidth, pageHeight, 'F');

                const themeColor = hexToRgba(primaryColor, 0.5).match(/\d+/g);
                if (themeColor) {
                    const r = parseInt(themeColor[0]);
                    const g = parseInt(themeColor[1]);
                    const b = parseInt(themeColor[2]);

                    pdfDoc.setFillColor(r, g, b);
                    pdfDoc.setDrawColor(r, g, b);
                    pdfDoc.setLineWidth(0.2);

                    const dotSize = 0.3;
                    const dotSpacing = 8;

                    for (let x = dotSpacing; x < pageWidth - dotSpacing; x += dotSpacing) {
                        for (let y = dotSpacing; y < pageHeight - dotSpacing; y += dotSpacing) {
                            pdfDoc.circle(x, y, dotSize, 'F');
                        }
                    }
                }

                pdfDoc.setFillColor(255, 255, 255);
                pdfDoc.setTextColor(0, 0, 0);
            };

            const headerHTML = `
                <div style="margin-bottom: 3px; padding-bottom: 5px; text-align: center; border-bottom: 2px solid ${hexToRgba(primaryColor, 0.05)}">
                    <div style="margin-bottom: 3px;">
                        <h1 style="color: ${primaryColor}; font-size: 22px; margin: 0 0 5px 0; font-weight: 800; letter-spacing: ${isRTL ? 'normal' : '-0.5px'};">
                            ${businessInfo.businessname || (translations.returnList || 'Fabric Return List')}
                        </h1>
                        <div style="color: #666; font-size: 11px; font-weight: 500; margin-top: 3px;">
                            ${translations.allReturns || 'Returns Report'} - ${new Date().toISOString().split('T')[0]}
                            ${currentShop ? ` | Shop: ${currentShop.name}` : ''}
                        </div>
                        ${businessInfo.businessaddress ? `<div style="color: #888; font-size: 9px; margin-top: 2px;">${businessInfo.businessaddress}</div>` : ''}
                        <div style="color: #888; font-size: 9px; margin-top: 2px;">
                            ${translations.printedBy || 'Generated by'}: ${user?.fullname || user?.username || 'System'}
                        </div>
                    </div>
                    ${summaryStatsHTML}
                </div>
            `;

            const headerCellStyle = `
                padding: 8px 6px; 
                border: none; 
                text-align: center; 
                font-weight: 600; 
                font-size: ${pdfOrientation === 'portrait' ? '8px' : '7px'}; 
                border-right: 1px solid rgba(255,255,255,0.1);
            `;

            const tableHeaderHTML = `
                <table style="width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 5px; font-size: ${pdfOrientation === 'portrait' ? '7px' : '6px'}; border-radius: 5px; overflow: hidden; border: 1px solid ${hexToRgba(primaryColor, 0.15)};">
                    <thead>
                        <tr style="background: linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.9)}); color: white;">
                            <th style="${headerCellStyle}">${translations.serialNo || 'No.'}</th>
                            <th style="${headerCellStyle}">${translations.returnNumber || 'Return #'}</th>
                            <th style="${headerCellStyle}">${translations.returnDate || 'Date'}</th>
                            <th style="${headerCellStyle}">${translations.originalBillNumber || 'Original Bill'}</th>
                            <th style="${headerCellStyle}">${translations.customerName || 'Customer'}</th>
                            <th style="${headerCellStyle}">${translations.refundAmount || 'Refund'}</th>
                            <th style="${headerCellStyle}">${translations.totalYards || 'Total Yards'}</th>
                            <th style="${headerCellStyle}">${translations.totalMeters || 'Total Meters'}</th>
                            <th style="${headerCellStyle}">${translations.rolls || 'Rolls'}</th>
                            <th style="${headerCellStyle}">${translations.paymentMethod || 'Payment'}</th>
                            <th style="${headerCellStyle.replace('border-right:', 'border: none;')}">${translations.addedBy || 'Cashier'}</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            for (let pageNum = 1; pageNum <= totalPagesCount; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, returnsToExport.length);
                const pageReturns = returnsToExport.slice(startRow, endRow);

                const tableRowsHTML = pageReturns.map((ret, index) => {
                    const itemTotals = calculateItemTotals(ret.items);
                    
                    return `
                        <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : 'background-color: #ffffff;'}">
                            <td style="padding: 5px; border: none; text-align: center; font-weight: 600; color: ${primaryColor}; border-bottom: 1px solid #f0f0f0;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; background: ${hexToRgba(primaryColor, 0.1)}; border-radius: 3px; font-size: 8px;">
                                    ${startRow + index + 1}
                                </div>
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; font-weight: 500; border-bottom: 1px solid #f0f0f0; font-size: 7px; font-family: monospace;">
                                ${ret.return_number || '-'}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 7px;">
                                ${formatDate(ret.return_date)}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 7px; font-family: monospace;">
                                ${ret.original_bill_number || '-'}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 7px;">
                                <div style="font-weight: 500;">${ret.customer_name || '-'}</div>
                                ${ret.customer_phone ? `<div style="font-size: 6px; color: #6b7280;">${ret.customer_phone}</div>` : ''}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; font-weight: 700; color: #dc2626; border-bottom: 1px solid #f0f0f0; font-size: 7px;">
                                ${formatCurrency(ret.refund_amount)}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 7px;">
                                ${formatNumber(itemTotals.totalYards)}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 7px;">
                                ${formatNumber(itemTotals.totalMeters)}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 7px;">
                                ${itemTotals.totalRolls}
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 7px;">
                                <div style="display: inline-block; padding: 2px 8px; background: #e0e7ff; border-radius: 12px; color: #4f46e5; font-size: 6px;">
                                    ${translations[ret.payment_method] || ret.payment_method || '-'}
                                </div>
                            </td>
                            <td style="padding: 5px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 7px;">
                                <div style="display: inline-block; padding: 2px 8px; background: #d1fae5; border-radius: 12px; color: #059669;">
                                    ${ret.username || '-'}
                                </div>
                            </td>
                        </tr>
                    `;
                }).join('');

                const tableHTML = tableHeaderHTML + tableRowsHTML + '</tbody></table>';

                const pageHTML = `
                    <div style="width: 800px; padding: 15px 20px; background-color: rgba(255, 255, 255, 0.98); font-family: ${isRTL ? "'Noto Naskh Arabic', 'Segoe UI', Tahoma" : "'Inter', 'Segoe UI', sans-serif"}; direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'}; color: #1a1a1a; border-radius: 4px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        ${headerHTML}
                        ${filterInfoHTML}
                        <div style="overflow-x: auto;">
                            ${tableHTML}
                        </div>
                    </div>
                `;

                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.top = '0';
                pdfContainer.style.width = '800px';
                pdfContainer.innerHTML = pageHTML;
                document.body.appendChild(pdfContainer);

                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false
                });

                document.body.removeChild(pdfContainer);

                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                if (pageNum > 1) {
                    pdf.addPage();
                }

                addPatternBackground(pdf);
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pageHeight - 20));

                pdf.setFontSize(8);
                pdf.setTextColor(120, 120, 120);
                pdf.text(`Page ${pageNum} of ${totalPagesCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
            }

            const fileName = `returns_${currentShop?.name?.replace(/[^a-z0-9]/gi, '_') || 'report'}_${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName);

            showToast('success', `PDF exported successfully with ${returnsToExport.length} return records across ${totalPagesCount} pages!`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', 'Error generating PDF. Please try again.');
        } finally {
            setPdfLoading(false);
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faUndo} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.returnList || 'Fabric Return List'}</h1>
                        <p>
                            {user ? `${translations.allReturns || 'All Returns'} - ${user.username}` : translations.allReturns || 'All Returns'}
                            {currentShop && <span style={{ marginLeft: '10px', color: 'var(--primary-color)' }}> | {currentShop.name}</span>}
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All Time'}</option>
                            <option value="today">{translations.todayReturns || 'Today'}</option>
                            <option value="week">{translations.thisWeek || 'This Week'}</option>
                            <option value="month">{translations.thisMonth || 'This Month'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact date-filter">
                        <input
                            ref={startDateRef}
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={getTodayDate()}
                            className="filter-input compact date-input"
                            title={translations.startDate || 'Start Date'}
                            placeholder="Start Date"
                        />
                    </div>

                    <div className="filter-group compact date-filter">
                        <input
                            ref={endDateRef}
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={getTodayDate()}
                            className="filter-input compact date-input"
                            title={translations.endDate || 'End Date'}
                            placeholder="End Date"
                        />
                    </div>

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search returns...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchReturns}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={resetFilters}
                            className="refresh-btn-modern compact"
                            title={translations.resetFilters || 'Reset Filters'}
                            style={{ background: '#6b7280' }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            <span className="btn-text">{translations.resetFilters || 'Reset'}</span>
                        </button>
                    </div>

                    <div className="filter-group compact">
                        <select
                            value={pdfOrientation}
                            onChange={(e) => setPdfOrientation(e.target.value)}
                            className="filter-select compact"
                            title={translations.orientation || 'Orientation'}
                            style={{ minWidth: '100px' }}
                        >
                            <option value="portrait">{translations.portrait || 'Portrait'}</option>
                            <option value="landscape">{translations.landscape || 'Landscape'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact">
                        <select
                            value={pdfPageSize}
                            onChange={(e) => setPdfPageSize(e.target.value)}
                            className="filter-select compact"
                            title={translations.pageSize || 'Page Size'}
                            style={{ minWidth: '90px' }}
                        >
                            <option value="a4">{translations.a4 || 'A4'}</option>
                            <option value="letter">{translations.letter || 'Letter'}</option>
                            <option value="legal">{translations.legal || 'Legal'}</option>
                            <option value="a5">{translations.a5 || 'A5'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={handleExportPDF}
                            className="refresh-btn-modern compact"
                            disabled={loading || filteredReturns.length === 0 || pdfLoading}
                            title={translations.exportToPDF || 'Export PDF'}
                            style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
                        >
                            {pdfLoading ? (
                                <div className="btn-spinner-small"></div>
                            ) : (
                                <FontAwesomeIcon icon={faFilePdf} />
                            )}
                            <span className="btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingReturns || 'Loading returns...'}</p>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.returnNumber || 'Return #'}</th>
                                            <th className="centered-cell">{translations.returnDate || 'Date'}</th>
                                            <th className="centered-cell">{translations.originalBillNumber || 'Original Invoice'}</th>
                                            <th className="name-column centered-cell">{translations.customerName || 'Customer'}</th>
                                            <th className="centered-cell">{translations.refundAmount || 'Refund'}</th>
                                            <th className="centered-cell">{translations.returnReason || 'Reason'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Cashier'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentReturns.map((returnRecord, index) => {
                                            const itemTotals = calculateItemTotals(returnRecord.items);
                                            return (
                                                <tr key={returnRecord.id}>
                                                    <td className="id-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                            <span className="cell-text">{startIndex + index + 1}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faReceipt} className="cell-icon" />
                                                            <span className="cell-text" style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                                                                {returnRecord.return_number}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faCalendarAlt} className="cell-icon" />
                                                            <span className="cell-text">{formatDate(returnRecord.return_date)}</span>
                                                        </div>
                                                    {/* </td */}
                                                                                                        </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faFileInvoice} className="cell-icon" />
                                                            <span className="cell-text" style={{ fontFamily: 'monospace' }}>
                                                                {returnRecord.original_bill_number}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="name-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faUser} className="cell-icon" />
                                                            <span className="cell-text">{returnRecord.customer_name || 'Walk-in Customer'}</span>
                                                            {returnRecord.customer_phone && (
                                                                <div className="cell-subtext">
                                                                    <FontAwesomeIcon icon={faPhone} size="xs" />
                                                                    <span>{returnRecord.customer_phone}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="refund-amount" style={{ color: '#dc2626', fontWeight: 'bold' }}>
                                                                {formatCurrency(returnRecord.refund_amount)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                  
                                                    
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="reason-text" style={{
                                                                maxWidth: '150px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {returnRecord.return_reason?.substring(0, 30) || '-'}
                                                                {returnRecord.return_reason?.length > 30 ? '...' : ''}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="added-by-badge">
                                                                <FontAwesomeIcon icon={faUser} className="cell-icon" size="xs" />
                                                                <span className="cell-text">{returnRecord.username}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="cell-content centered-content actions-content">
                                                            <button
                                                                onClick={() => handleView(returnRecord)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredReturns.length > 0 && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredReturns.length)} {translations.of || 'of'} {filteredReturns.length} {translations.returns || 'returns'}</span>
                                <span style={{ marginLeft: '20px', color: '#059669', fontWeight: '600' }}>
                                    {translations.totalRefundAmount || 'Total Refund'}: {formatCurrency(totalRefundAmount)}
                                </span>
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}

                    {!loading && filteredReturns.length === 0 && (
                        <div className="empty-state">
                            <FontAwesomeIcon icon={faUndo} className="empty-icon" />
                            <p>{translations.noReturnsFound || 'No returns found'}</p>
                        </div>
                    )}
                </>
            )}

            {/* View Modal - MOVABLE, RESIZABLE */}
            {showViewModal && selectedReturn && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div 
                        ref={modalRef}
                        className="modal-content-modern edit-modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        {/* Draggable Header */}
                        <div className="modal-header-modern" onMouseDown={startDrag} style={{ cursor: 'move', flexShrink: 0 }}>
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faUndo} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.returnDetails || 'Return Details'}</h3>
                                <span className="return-number-badge">{selectedReturn.return_number}</span>
                            </div>
                            
                            {/* Width Control Buttons */}
                            <div className="width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button
                                        key={key}
                                        className={`width-btn ${currentWidthMode === key ? 'active' : ''}`}
                                        onClick={() => setWidthMode(key)}
                                        title={translations[mode.label.toLowerCase()] || mode.label}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            
                            <button className="close-btn-modern" onClick={() => setShowViewModal(false)} type="button">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="modal-body-modern" style={{ flex: 1, overflow: 'auto' }}>
                            {/* Return Header Info */}
                            <div className="return-header-info" style={{ 
                                background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                                borderRadius: '12px',
                                padding: '15px',
                                marginBottom: '20px'
                            }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                                    <div>
                                        <strong><FontAwesomeIcon icon={faReceipt} /> {translations.returnNumber || 'Return Number'}:</strong>
                                        <div style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                            {selectedReturn.return_number}
                                        </div>
                                    </div>
                                    <div>
                                        <strong><FontAwesomeIcon icon={faCalendarAlt} /> {translations.returnDate || 'Return Date'}:</strong>
                                        <div>{formatDate(selectedReturn.return_date)}</div>
                                    </div>
                                    <div>
                                        <strong><FontAwesomeIcon icon={faFileInvoice} /> {translations.originalBillNumber || 'Original Invoice'}:</strong>
                                        <div style={{ fontFamily: 'monospace' }}>{selectedReturn.original_bill_number}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Customer Info */}
                            {(selectedReturn.customer_name || selectedReturn.customer_phone || selectedReturn.customer_gst) && (
                                <div className="detail-section" style={{ marginBottom: '20px' }}>
                                    <h4 style={{ marginBottom: '10px', color: 'var(--primary-color)' }}>
                                        <FontAwesomeIcon icon={faUser} /> {translations.customerDetails || 'Customer Details'}
                                    </h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', background: '#f9fafb', padding: '15px', borderRadius: '8px' }}>
                                        {selectedReturn.customer_name && (
                                            <div><strong>{translations.customerName || 'Name'}:</strong> {selectedReturn.customer_name}</div>
                                        )}
                                        {selectedReturn.customer_phone && (
                                            <div><strong>{translations.customerPhone || 'Phone'}:</strong> {selectedReturn.customer_phone}</div>
                                        )}
                                        {selectedReturn.customer_gst && (
                                            <div><strong>{translations.customerGST || 'GST'}:</strong> {selectedReturn.customer_gst}</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Items Returned with Rolls, Yards, Meters */}
                            {selectedReturn.items && selectedReturn.items.length > 0 && (
                                <div className="detail-section" style={{ marginBottom: '20px' }}>
                                    <h4 style={{ marginBottom: '10px', color: 'var(--primary-color)' }}>
                                        <FontAwesomeIcon icon={faBoxes} /> {translations.itemsReturnedList || 'Items Returned'}
                                    </h4>
                                    <div className="table-scroll-container" style={{ maxHeight: '300px', overflow: 'auto' }}>
                                        <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: '8px', textAlign: 'left' }}>{translations.productName || 'Product'}</th>
                                                    <th style={{ padding: '8px', textAlign: 'center' }}>{translations.quantity || 'Qty'}</th>
                                                    <th style={{ padding: '8px', textAlign: 'center' }}>{translations.rolls || 'Rolls'}</th>
                                                    <th style={{ padding: '8px', textAlign: 'center' }}>{translations.totalYards || 'Total Yards'}</th>
                                                    <th style={{ padding: '8px', textAlign: 'center' }}>{translations.totalMeters || 'Total Meters'}</th>
                                                    <th style={{ padding: '8px', textAlign: 'right' }}>{translations.price || 'Price'}</th>
                                                    <th style={{ padding: '8px', textAlign: 'right' }}>{translations.total || 'Total'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedReturn.items.map((item, idx) => {
                                                    const itemQty = parseFloat(item.quantity) || 0;
                                                    const itemRolls = parseInt(item.rolls) || 0;
                                                    const itemYards = item.unit === 'yards' ? itemQty : itemQty * 1.09361;
                                                    const itemMeters = item.unit === 'meters' ? itemQty : itemQty * 0.9144;
                                                    
                                                    return (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                            <td style={{ padding: '8px' }}>
                                                                <div><strong>{item.product_name}</strong></div>
                                                                {item.product_colorcode && <div style={{ fontSize: '11px', color: '#6b7280' }}>Color: {item.product_colorcode}</div>}
                                                                {item.batch_no && <div style={{ fontSize: '11px', color: '#6b7280' }}>Batch: {item.batch_no}</div>}
                                                            </td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{itemQty.toFixed(2)} {item.unit}</td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{itemRolls}</td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{formatNumber(itemYards)} yds</td>
                                                            <td style={{ padding: '8px', textAlign: 'center' }}>{formatNumber(itemMeters)} m</td>
                                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(item.price_per_unit)}</td>
                                                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(item.total)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ background: '#f1f5f9', fontWeight: 'bold' }}>
                                                    <td colSpan="2" style={{ padding: '8px', textAlign: 'right' }}>Totals:</td>
                                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                                        {selectedReturn.items.reduce((sum, item) => sum + (parseInt(item.rolls) || 0), 0)}
                                                    </td>
                                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                                        {formatNumber(selectedReturn.items.reduce((sum, item) => {
                                                            const qty = parseFloat(item.quantity) || 0;
                                                            const yards = item.unit === 'yards' ? qty : qty * 1.09361;
                                                            return sum + yards;
                                                        }, 0))} yds
                                                    </td>
                                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                                        {formatNumber(selectedReturn.items.reduce((sum, item) => {
                                                            const qty = parseFloat(item.quantity) || 0;
                                                            const meters = item.unit === 'meters' ? qty : qty * 0.9144;
                                                            return sum + meters;
                                                        }, 0))} m
                                                    </td>
                                                    <td colSpan="2"></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Financial Summary */}
                            <div className="detail-section" style={{ marginBottom: '20px' }}>
                                <h4 style={{ marginBottom: '10px', color: 'var(--primary-color)' }}>
                                    <FontAwesomeIcon icon={faCalculator} /> {translations.returnCalculations || 'Return Summary'}
                                </h4>
                                <div style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                                        <span>{translations.subtotal || 'Subtotal'}:</span>
                                        <span>{formatCurrency(selectedReturn.subtotal)}</span>
                                    </div>
                                    {parseFloat(selectedReturn.discount_amount) > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#dc2626' }}>
                                            <span>{translations.discountAmount || 'Discount'}:</span>
                                            <span>-{formatCurrency(selectedReturn.discount_amount)}</span>
                                        </div>
                                    )}
                                    {parseFloat(selectedReturn.tax_amount) > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#059669' }}>
                                            <span>{translations.taxAmount || 'Tax'}:</span>
                                            <span>+{formatCurrency(selectedReturn.tax_amount)}</span>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #e2e8f0', fontWeight: 'bold' }}>
                                        <span>{translations.grandTotal || 'Grand Total'}:</span>
                                        <span>{formatCurrency(selectedReturn.grand_total)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #dc2626', fontWeight: 'bold', color: '#dc2626' }}>
                                        <span>{translations.refundAmount || 'Refund Amount'}:</span>
                                        <span>{formatCurrency(selectedReturn.refund_amount)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', marginTop: '10px' }}>
                                        <span>{translations.paymentMethod || 'Payment Method'}:</span>
                                        <span style={{ textTransform: 'uppercase' }}>{selectedReturn.payment_method}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Return Reason & Notes */}
                            {selectedReturn.return_reason && (
                                <div className="detail-section" style={{ marginBottom: '15px' }}>
                                    <h4 style={{ marginBottom: '10px', color: 'var(--primary-color)' }}>
                                        <FontAwesomeIcon icon={faHistory} /> {translations.returnReason || 'Return Reason'}
                                    </h4>
                                    <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', color: '#92400e' }}>
                                        {selectedReturn.return_reason}
                                    </div>
                                </div>
                            )}

                            {selectedReturn.notes && (
                                <div className="detail-section" style={{ marginBottom: '15px' }}>
                                    <h4 style={{ marginBottom: '10px', color: 'var(--primary-color)' }}>
                                        <FontAwesomeIcon icon={faInfoCircle} /> {translations.notes || 'Notes'}
                                    </h4>
                                    <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '8px', fontStyle: 'italic' }}>
                                        {selectedReturn.notes}
                                    </div>
                                </div>
                            )}

                            {/* Added By Info */}
                            <div className="detail-section">
                                <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                                    <FontAwesomeIcon icon={faUser} /> {translations.addedBy || 'Processed by'}: {selectedReturn.username}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer-modern" style={{ flexShrink: 0 }}>
                            <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.close || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FabReturnList;